import { BlogClusterSeo } from "@/components/blog/BlogSeo";
import {
  ClusterPageShell,
  ClusterPageSkeleton,
} from "@/components/blog/ClusterPageShell";
import { ClusterPillarCard } from "@/components/blog/ClusterPillarCard";
import { PostCard } from "@/components/blog/PostCard";
import { PublicRoutes } from "@/constants/routes";
import { Alert, Button, Card, EmptyState, Heading, Text } from "@/design-system";
import { usePublishedCluster } from "@/hooks/blog";
import { NotFound } from "@/pages/notFound/notFound";
import { Home } from "lucide-react";
import { Link, useParams } from "react-router";

/**
 * Hub público de um cluster de conteúdo (`/blog/cluster/<slug>`).
 *
 * O SSR já entrega esta página montada (ver `renderClusterBody`); esta versão
 * existe para hidratar por cima e para a navegação client-side dentro do SPA
 * não cair em tela em branco. Por isso a estrutura é deliberadamente a mesma:
 * trilha, H1 igual à `headKeyword`, pilar destacado antes dos satélites e o
 * link de conversão fechando a página. Divergir aqui faria o usuário ver a
 * página mudar de forma no instante em que o React assume.
 */
export const BlogClusterPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError, refetch, isFetching } = usePublishedCluster(slug);

  if (isLoading) return <ClusterPageSkeleton />;

  // Só sem dados: com o hub em mãos (do SSR), falha na revalidação em segundo
  // plano não troca a página pronta pelo aviso de erro.
  if (isError && !data) {
    return (
      <ClusterPageShell>
        <Alert tone="danger" title="Não foi possível carregar este tema">
          <div className="flex flex-col items-start gap-3">
            <Text size="small" tone="muted">
              Verifique sua conexão e tente de novo. Se continuar, os conteúdos seguem
              disponíveis na listagem do blog.
            </Text>
            <Button
              variant="secondary"
              isLoading={isFetching}
              loadingLabel="Carregando o tema"
              onClick={() => refetch()}
            >
              Tentar de novo
            </Button>
          </div>
        </Alert>
      </ClusterPageShell>
    );
  }

  // Cluster inexistente, arquivado ou despublicado: é 404 de verdade, e a URL
  // precisa continuar a mesma para o buscador ver o status da página que ele
  // pediu — por isso renderiza o 404 em vez de redirecionar.
  if (!data) return <NotFound />;

  const { cluster, posts } = data;
  const pillar = posts.find(p => p.id === cluster.pillarPostId) ?? null;
  const satellites = posts.filter(p => p.id !== pillar?.id);

  return (
    <>
      <BlogClusterSeo cluster={cluster} posts={posts} />
      <ClusterPageShell>
        <nav aria-label="Trilha de navegação" className="mb-6">
          <ol className="text-ds-small text-ds-ink-600 font-ds-sans flex flex-wrap items-center gap-1.5">
            <li>
              <Link
                to={PublicRoutes.HOME}
                className="rounded-ds-sm hover:text-ds-brand-700 focus-visible:ring-ds-focus inline-flex cursor-pointer items-center gap-1 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <Home size={14} aria-hidden="true" />
                Início
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link
                to={PublicRoutes.BLOG}
                className="rounded-ds-sm hover:text-ds-brand-700 focus-visible:ring-ds-focus cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                Blog
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-ds-ink-800" aria-current="page">
              {cluster.name}
            </li>
          </ol>
        </nav>

        {/* O H1 é a headKeyword, não o nome interno do cluster: quem chega aqui
            pelo buscador digitou a keyword, e o título tem de devolver
            exatamente isso. */}
        <header className="flex max-w-prose flex-col gap-3">
          <Heading level={1}>{cluster.headKeyword}</Heading>
          {cluster.description && (
            <Text size="lead" tone="muted">
              {cluster.description}
            </Text>
          )}
        </header>

        {posts.length === 0 ? (
          <EmptyState
            className="mt-10"
            title="Ainda não há conteúdos publicados neste tema"
            description="Estamos preparando os materiais. Enquanto isso, veja o que já está no ar no blog."
            action={
              <Button asChild variant="secondary" navigates="internal" fullWidth className="sm:w-auto">
                <Link to={PublicRoutes.BLOG}>Ver todos os conteúdos</Link>
              </Button>
            }
          />
        ) : (
          <>
            {pillar && (
              <section aria-labelledby="cluster-pilar" className="mt-10">
                <Heading level={2} size={3} id="cluster-pilar">
                  Comece por aqui
                </Heading>
                <div className="mt-4">
                  <ClusterPillarCard post={pillar} />
                </div>
              </section>
            )}

            {satellites.length > 0 && (
              <section aria-labelledby="cluster-satelites" className="mt-12">
                <Heading level={2} size={3} id="cluster-satelites">
                  Todos os conteúdos sobre {cluster.headKeyword}
                </Heading>
                <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {satellites.map(post => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* O hub existe para levar tráfego de tema até a LP que converte; sem
            este fecho a página seria só um índice. */}
        <Card className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex max-w-prose flex-col gap-1">
            <Heading level={2} size={4}>
              Coloque isso em prática na sua loja
            </Heading>
            <Text size="small" tone="muted">
              A Fidexa organiza a cobrança do seu crediário e traz o cliente de volta ao
              balcão.
            </Text>
          </div>
          <Button asChild navigates="internal" fullWidth className="sm:w-auto sm:shrink-0">
            <Link to={cluster.targetLandingPath}>Conhecer a Fidexa</Link>
          </Button>
        </Card>
      </ClusterPageShell>
    </>
  );
};
